/**
 * UUID v7 generation with Base58 short-encoding.
 *
 * Provides a {@link UUID} class backed by 128-bit byte arrays with methods
 * to convert between standard string form (`00000000-0000-0000-0000-000000000000`),
 * a compact Base58 short form, and raw bytes.
 *
 * @module uuid
 */

/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as uuid from "uuid";

const UUID_SIZE = 16;

// Flickr-style Base58
const ALPHABET = "123456789abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ";
const BASE = ALPHABET.length;

// Bit shifts for the timestamp
const SHIFT_40 = Math.pow(2, 40);
const SHIFT_32 = Math.pow(2, 32);
const SHIFT_24 = Math.pow(2, 24);
const SHIFT_16 = Math.pow(2, 16);
const SHIFT_8 = Math.pow(2, 8);

// Fast char->value map (ASCII only)
const CHAR_MAP = (() => {
  const m = new Int8Array(128);
  m.fill(-1);
  for (let i = 0; i < ALPHABET.length; i++) {
    m[ALPHABET.charCodeAt(i)] = i;
  }
  return m;
})();

function encodeShort(uuid: Uint8Array): string {
  if (uuid.length !== UUID_SIZE) {
    throw new Error("Invalid UUID length");
  }

  // Count leading zeros
  let zeros = 0;
  while (zeros < UUID_SIZE && uuid[zeros] === 0) zeros++;

  // We store digits little-endian in this dynamic array.
  const digits: number[] = [];

  for (let i = zeros; i < UUID_SIZE; i++) {
    let carry = uuid[i]!;
    for (let j = 0; j < digits.length; j++) {
      const x = (digits[j]! << 8) + carry;
      digits[j] = x % BASE;
      carry = (x / BASE) | 0;
    }
    while (carry > 0) {
      digits.push(carry % BASE);
      carry = (carry / BASE) | 0;
    }
  }

  // Produce string: leading zeros, then digits reversed
  let out = "";
  for (let i = digits.length - 1; i >= 0; i--) out += ALPHABET[digits[i]!]!;
  return out;
}

function decodeShort(short: string): Uint8Array {
  // big-endian 128-bit integer, initialized to 0
  const out = new Uint8Array(UUID_SIZE);
  const length = short.length;

  for (let k = 0; k < length; k++) {
    const c = short.charCodeAt(k);
    const val = CHAR_MAP[c];

    if (val == null || val < 0) {
      throw new Error("Invalid Base58 character");
    }

    // out = out * BASE + val
    let carry = val;
    for (let i = UUID_SIZE - 1; i >= 0; i--) {
      const x = out[i]! * BASE + carry;
      out[i] = x & 0xff;
      carry = x >>> 8;
    }

    if (carry !== 0) {
      // Would require more than UUID_SIZE bytes -> overflow
      throw new Error("Base58 value exceeds 128-bit (UUID) size");
    }
  }

  return out;
}

/**
 * A UUID backed by a 128-bit byte array. Supports v7 generation and conversion
 * between standard string form, Base58 short form, and raw bytes.
 *
 * @example
 * ```ts
 * const id = UUID.generate();
 * id.toString();  // "01943b3c-..."
 * id.toShort();   // "6Xb9hW..."
 * ```
 */
export class UUID {
  /** The raw 16-byte representation. */
  readonly bytes: Readonly<Uint8Array>;

  protected constructor(bytes: Uint8Array) {
    this.bytes = bytes;
  }

  /**
   * Generates a new v7 UUID.
   *
   * @param options - Options forwarded to the underlying `uuid.v7` call.
   */
  static generate(options?: uuid.Version7Options): UUID {
    return new this(uuid.v7(options, new Uint8Array(UUID_SIZE)));
  }

  /**
   * Generates a new v7 UUID and returns its Base58 short form.
   */
  static generateShort(): string {
    return this.generate().toShort();
  }

  /**
   * Generates a new v7 UUID and returns its standard string form.
   */
  static generateString(): string {
    return this.generate().toString();
  }

  /**
   * Creates a UUID from a raw 16-byte array.
   *
   * @param bytes - Exactly 16 bytes.
   * @throws If the array length isn't 16.
   */
  static fromBytes(bytes: Uint8Array): UUID {
    if (bytes.length !== UUID_SIZE) {
      throw new Error("Invalid UUID length");
    }
    return new this(bytes);
  }

  /**
   * Decodes a Base58-encoded short UUID back into a {@link UUID} instance.
   *
   * @param short - The Base58 string to decode.
   */
  static fromShort(short: string): UUID {
    return new this(decodeShort(short));
  }

  /**
   * Encodes this UUID as a compact Base58 string.
   */
  toShort(): string {
    return encodeShort(this.bytes);
  }

  /**
   * Parses a standard UUID string (for example, `"01943b3c-..."`) into a
   * {@link UUID} instance.
   *
   * @param string - The UUID string to parse.
   */
  static fromString(string: string): UUID {
    return new this(uuid.parse(string));
  }

  /**
   * Parses either a standard hyphenated UUID string or a Base58 short form.
   *
   * @param value - The UUID string to parse, in either form.
   */
  static from(value: string): UUID {
    return value.length === 36 ? this.fromString(value) : this.fromShort(value);
  }

  /**
   * Returns the standard hyphenated UUID string representation.
   */
  toString(): string {
    return uuid.stringify(this.bytes);
  }

  /**
   * Returns the UUID version number (for example, `7` for v7).
   */
  version(): number {
    return this.bytes[6]! >>> 4;
  }

  /**
   * Extracts the Unix-epoch millisecond timestamp embedded in a v7 UUID.
   *
   * @returns The timestamp in milliseconds.
   * @throws If the UUID version doesn't support embedded timestamps.
   */
  timestamp(): number {
    switch (this.version()) {
      case 7:
        return (
          this.bytes[0]! * SHIFT_40 +
          this.bytes[1]! * SHIFT_32 +
          this.bytes[2]! * SHIFT_24 +
          this.bytes[3]! * SHIFT_16 +
          this.bytes[4]! * SHIFT_8 +
          this.bytes[5]!
        );
      default:
        throw new Error(`UUID version ${this.version()} is not supported`);
    }
  }

  /**
   * Compares two UUIDs byte-by-byte.
   *
   * @param other - The UUID to compare against.
   * @returns `true` if every byte matches.
   */
  equals(other: UUID): boolean {
    return this.bytes.every((byte, index) => byte === other.bytes[index]);
  }
}
